import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaShoppingCart, FaFire, FaCheckCircle, FaStar, FaMapMarkerAlt } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import ProfileActionsMenu from './ProfileActionsMenu';
import { UnifiedSpotlightModal } from '@/components/UnifiedSpotlightModal';
import { SpotlightSuccessModal } from '@/components/SpotlightSuccessModal';
import SpotlightCountdown from '@/components/SpotlightCountdown/SpotlightCountdown';
import { useProfileSpotlight } from '@/hooks/useProfileSpotlight';
import { useAppSelector, useAppDispatch } from '@/redux/Store';
import { updateProfileSpotlight } from '@/redux/slices/marketplaceSlice';
import { fetchUserBalance } from '@/redux/thunks/marketplaceThunks';
import { getMediaUrl } from '@/constants/media';
import { formatName, nameInitial } from '@/utils/formatName';
import { CreatorRankBadge } from '@/components/CreatorRankBadge';
import { profileLink } from '@/utils/profileLink';
import { toast } from 'react-toastify';
import './ProfileCard.scss';

export interface ProfileListing {
  id: string;
  // Creator info (primary)
  creatorId: string;
  creatorUsername: string;
  creatorDisplayName: string;
  creatorAvatarUrl?: string;
  // Owner info (secondary)
  ownerId: string;
  ownerUsername: string;
  ownerDisplayName: string;
  ownerAvatarUrl?: string;
  // Profile data
  price: number;
  klyxValue: number;
  trendingScore: number;
  purchaseCount: number;
  // Creator's home location, or null if they haven't set one
  creatorLocation: {
    city: string | null;
    state: string | null;
    countryCode: string;
  } | null;
  badges: {
    verified?: boolean;
    topProfile?: boolean;
    trending?: boolean;
    spotlighted?: boolean;
  };
  // Spotlight info
  spotlight?: {
    /** spotlight_promotion.id — passed as source_ref when this card is
     *  clicked while spotlighted, so analytics can attribute the view
     *  to the specific paid promotion. */
    id?: string;
    startTime: string;
    endTime: string;
  } | null;
}

interface ProfileCardProps {
  profile: ProfileListing;
  onBuyClick: (profile: ProfileListing) => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile, onBuyClick }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const isEmailUser = user?.authMethod === 'email';

  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedDuration, setCompletedDuration] = useState(24);
  const [spotlightError, setSpotlightError] = useState<string | null>(null);

  const {
    prepareTransaction,
    executeTransaction,
    recordPurchase,
    purchaseSpotlight,
    reset,
  } = useProfileSpotlight();

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatLocation = (loc: NonNullable<ProfileListing['creatorLocation']>): string => {
    // City, State, Country — but skip nulls so a country-only profile
    // doesn't render as ", , US". Country is always present (we use
    // it as the existence gate server-side).
    return [loc.city, loc.state, loc.countryCode].filter(Boolean).join(', ');
  };

  const formatPrice = (price: number): string => {
    // Handle small decimals that would show in scientific notation
    if (price < 1) {
      return price.toFixed(8).replace(/\.?0+$/, ''); // Show up to 8 decimals, remove trailing zeros
    }
    // For larger prices, show 2 decimal places
    return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleSpotlightClick = () => {
    setShowModal(true);
  };

  const handleDurationChange = async (duration: number): Promise<{
    price: number;
    canProceed?: boolean;
    warning?: string;
    balance?: { current: number; required: string; shortfall: string | null };
  }> => {
    try {
      const details = await prepareTransaction({ profileId: profile.id, durationHours: duration });
      return {
        price: parseFloat(details.totalCost) || 0,
        canProceed: details.canProceed,
        warning: details.warning,
        balance: details.balance,
      };
    } catch (err: any) {
      console.error('Failed to get price:', err);
      throw err;
    }
  };

  const handleConfirm = async (duration: number) => {
    setIsProcessing(true);
    setCompletedDuration(duration);

    try {
      if (isEmailUser) {
        toast.info('Processing spotlight purchase...');
        await purchaseSpotlight({ profileId: profile.id, durationHours: duration });
      } else {
        toast.info('Please sign the transaction in your Klever wallet...');
        const txHash = await executeTransaction();
        toast.info('Recording purchase...');
        await recordPurchase(txHash);
      }

      // Optimistically update the profile in Redux
      dispatch(updateProfileSpotlight({ profileId: profile.id, durationHours: duration }));

      // Refresh displayed marketplace balance — spotlight spend isn't reflected by the spotlight thunk.
      dispatch(fetchUserBalance());

      // Close spotlight modal and show success modal
      setShowModal(false);
      setShowSuccessModal(true);
      reset();
    } catch (err: any) {
      const message = err.message || 'Failed to spotlight profile';
      setSpotlightError(message);
      toast.error(message);
      console.error('Spotlight error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setSpotlightError(null);
    reset();
  };

  return (
    <div className={`profile-card ${profile.badges.spotlighted ? 'spotlighted' : ''}`}>
      {/* Header - Name and Actions */}
      <div className="profile-card-header">
        <div className="profile-card-title-block">
          <h3 className="profile-card-name">
            <a
              href={profileLink(
                profile.creatorUsername,
                profile.badges.spotlighted ? 'spotlight' : 'marketplace',
                profile.badges.spotlighted && profile.spotlight?.id
                  ? { ref: profile.spotlight.id }
                  : {},
              )}
              className="creator-link"
            >
              {formatName({ displayName: profile.creatorDisplayName, username: profile.creatorUsername })}
            </a>
            <CreatorRankBadge username={profile.creatorUsername} />
          </h3>
          {profile.creatorLocation && (
            <p className="profile-card-location" title={formatLocation(profile.creatorLocation)}>
              <FaMapMarkerAlt aria-hidden="true" />
              <span>{formatLocation(profile.creatorLocation)}</span>
            </p>
          )}
        </div>

        {/* Actions Menu */}
        <ProfileActionsMenu
          profile={profile}
          onBuyClick={onBuyClick}
          onSpotlightClick={handleSpotlightClick}
        />
      </div>

      {/* Avatar Section - Creator */}
      <div className="profile-card-avatar">
        {profile.creatorAvatarUrl ? (
          <img src={getMediaUrl(profile.creatorAvatarUrl) || undefined} alt={profile.creatorUsername} />
        ) : (
          <div className="avatar-placeholder">
            {nameInitial({ displayName: profile.creatorDisplayName, username: profile.creatorUsername })}
          </div>
        )}

        {/* Badges Overlay */}
        <div className="profile-card-badges">
          {profile.badges.spotlighted && (
            <span className="badge badge-spotlighted" title="Spotlighted">
              <HiSparkles />
            </span>
          )}
          {profile.badges.verified && (
            <span className="badge badge-verified" title="Verified">
              <FaCheckCircle />
            </span>
          )}
          {profile.badges.topProfile && (
            <span className="badge badge-top" title="Top Profile">
              <FaStar />
            </span>
          )}
          {profile.badges.trending && (
            <span className="badge badge-trending" title="Trending">
              <FaFire />
            </span>
          )}
        </div>
      </div>

      {/* Info Section */}
      <div className="profile-card-info">
        <p className="profile-card-owner">
          Owner <a href={profileLink(profile.ownerUsername, 'marketplace')} className="owner-link">{formatName({ displayName: profile.ownerDisplayName, username: profile.ownerUsername })}</a>
        </p>

        {/* Spotlight countdown — visible only while the spotlight is
            active. SpotlightCountdown renders null if endTime is
            missing or in the past. */}
        <SpotlightCountdown
          endTime={profile.spotlight?.endTime}
          className="profile-card-spotlight-countdown"
        />

        {/* Stats */}
        <div className="profile-card-stats">
          <div className="stat-item">
            <span className="stat-label">Klyx Value</span>
            <span className="stat-value">{formatNumber(profile.klyxValue)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Trending</span>
            <span className="stat-value">
              {profile.trendingScore > 0 ? formatNumber(profile.trendingScore) : '—'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Purchases</span>
            <span className="stat-value">{formatNumber(profile.purchaseCount)}</span>
          </div>
        </div>

        {/* Price and Buy Button */}
        <div className="profile-card-footer">
          <div className="profile-card-price">
            <span className="price-label">Price</span>
            <span className="price-value">{formatPrice(profile.price)}</span>
          </div>
          <button
            className="profile-card-buy-btn"
            onClick={() => onBuyClick(profile)}
            type="button"
          >
            <FaShoppingCart />
            <span>Buy</span>
          </button>
        </div>
      </div>

      {/* Unified Spotlight Modal - Rendered via Portal */}
      {ReactDOM.createPortal(
        <UnifiedSpotlightModal
          isOpen={showModal}
          title="Spotlight Profile"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          onDurationChange={handleDurationChange}
          isProcessing={isProcessing}
          defaultDuration={24}
          submissionError={spotlightError}
          onClearError={() => setSpotlightError(null)}
          explainer="Spotlighted profiles appear pinned at the top of the marketplace for anyone in the same state as the profile owner — for the duration you select. After the timer runs out the badge clears automatically."
        />,
        document.body
      )}

      {/* Success Modal - Rendered via Portal */}
      {ReactDOM.createPortal(
        <SpotlightSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title="Profile Spotlighted!"
          message={`${formatName({ displayName: profile.creatorDisplayName, username: profile.creatorUsername })} is now featured in the marketplace for ${completedDuration} hours! 🎉`}
          duration={3000}
        />,
        document.body
      )}
    </div>
  );
};

export default ProfileCard;
